import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import html2canvas from 'html2canvas';
import '../styles/ResultsStoryGenerator.css';

const themes = [
  { key: 'royal', name: 'جشن سلطنتی', className: 'royal', icon: '✦', image: 'https://images.unsplash.com/photo-1564399579883-451a5d44ec08?auto=format&fit=crop&w=900&q=85' },
  { key: 'sunset', name: 'غروب نارنجی', className: 'sunset', icon: '☼', image: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=900&q=85' },
  { key: 'midnight', name: 'شب مسابقه', className: 'midnight', icon: '⌁', image: 'https://images.unsplash.com/photo-1531512073830-ba890ca4eba2?auto=format&fit=crop&w=900&q=85' },
  { key: 'mint', name: 'مینت مدرن', className: 'mint', icon: '◌', image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=85' }
];

const typeLabels = { friend: 'دوستانه', family: 'خانوادگی', spouse: 'عاشقانه', colleague: 'همکاران', crush: 'کراش', follower: 'فالوورها' };

function ResultsStoryGenerator({ testType, topParticipants = [], totalParticipants = 0, averageScore = 0, creatorName, onClose }) {
  const [selectedTheme, setSelectedTheme] = useState('royal');
  const [generating, setGenerating] = useState(false);
  const storyRef = useRef(null);
  const currentTheme = themes.find((theme) => theme.key === selectedTheme) || themes[0];
  const participants = topParticipants.slice(0, 3);
  const podium = [participants[1], participants[0], participants[2]];

  const generateStory = async () => {
    if (!storyRef.current) return;
    setGenerating(true);
    try {
      await Promise.all([
        document.fonts?.load('900 100px Vazirmatn'),
        document.fonts?.load('700 40px Vazirmatn'),
        document.fonts?.ready
      ]);
      storyRef.current.classList.add('is-exporting');
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(storyRef.current, { width: 1080, height: 1920, scale: 1, backgroundColor: null, logging: false, useCORS: true, windowWidth: 1080, windowHeight: 1920 });
      storyRef.current.classList.remove('is-exporting');
      canvas.toBlob(async (blob) => {
        if (!blob) throw new Error('Result story image was not created');
        const file = new File([blob], `results-${Date.now()}.png`, { type: 'image/png' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: 'نتایج چقدر منو می‌شناسی؟', text: 'ببین کی بیشتر منو می‌شناسه!' });
            setGenerating(false);
            return;
          } catch (error) {
            if (error.name === 'AbortError') { setGenerating(false); return; }
          }
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = `results-${Date.now()}.png`;
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 200);
        setGenerating(false);
      }, 'image/png', 1);
    } catch (error) {
      storyRef.current?.classList.remove('is-exporting');
      console.error('Generate results story error:', error);
      alert('ساخت استوری با خطا روبه‌رو شد.');
      setGenerating(false);
    }
  };

  return createPortal((
    <div className="results-story-overlay" onClick={onClose}>
      <section className="results-story-modal" onClick={(event) => event.stopPropagation()}>
        <button className="results-close" onClick={onClose} aria-label="بستن"><span className="material-symbols-outlined">close</span></button>
        <header className="results-studio-header"><span>RESULTS STORY</span><h2>نتیجه را به یک لحظه تبدیل کن</h2><p>قالبی انتخاب کن که با حال‌وهوای آزمونت جور باشد.</p></header>
        <div className="results-studio-layout">
          <aside className="results-theme-panel"><strong>حالت نمایش</strong><div className="results-theme-list">{themes.map((theme) => <button key={theme.key} onClick={() => setSelectedTheme(theme.key)} className={`results-theme-card ${theme.className} ${selectedTheme === theme.key ? 'is-selected' : ''}`}><b>{theme.icon}</b><span>{theme.name}</span><i className="material-symbols-outlined">check</i></button>)}</div><p><span className="material-symbols-outlined">emoji_events</span> رتبه‌های اول در مرکز قاب دیده می‌شوند.</p></aside>
          <div className="results-preview-shell"><span>پیش‌نمایش 9:16</span><div className="results-story-preview"><div className="results-preview-stage"><div ref={storyRef} className={`results-story-canvas results-theme-${currentTheme.className}`}>
            <div className="results-light results-light-one" /><div className="results-light results-light-two" /><div className="results-confetti">✦　·　✦　·　✦</div>
            <div className="results-story-content">
              <div className="results-brand"><b>؟</b><span>چقدر منو می‌شناسی</span><i>{currentTheme.icon}</i></div>
              <div className="results-copy"><span>{typeLabels[testType] || 'چالش شخصی'} · پایان آزمون</span><h1 className="results-title-lines"><i>نتیجه‌ها</i><i>رسیدن!</i></h1><p>آدم‌هایی که <strong>{creatorName || 'تو'}</strong> را بهتر می‌شناسند</p></div>
              <div className="results-score-ring"><div><b>{Math.round(Number(averageScore) || 0)}</b><span>میانگین امتیاز</span></div><small>%</small></div>
              <div className="results-photo-frame"><img src={currentTheme.image} crossOrigin="anonymous" alt="تصویر جشن نتایج" onError={(event) => { event.currentTarget.parentElement.style.display = 'none'; }} /><span>{currentTheme.icon}</span></div>
              <div className="results-podium">{podium.map((participant, index) => { const rank = [2, 1, 3][index]; return <div key={`${participant?.name || 'empty'}-${rank}`} className={`podium-person podium-${rank}`}><div className="podium-avatar">{participant?.name?.trim()?.charAt(0) || '؟'}</div><b>{participant?.name || 'در انتظار...'}</b><small>{participant ? `${participant.score}%` : '—'}</small><i>{rank}</i></div>; })}</div>
              <div className="results-participants"><b>{totalParticipants || 0}</b><span>نفر در این چالش شرکت کردند</span><i>♥</i></div>
              <div className="results-footer"><span>تو هم چالش خودت را بساز</span><b>cheghadrmishnasi.com ↗</b></div>
            </div>
          </div></div></div></div>
        </div>
        <footer className="results-actions"><p><span className="material-symbols-outlined">ios_share</span> آماده برای استوری، پست یا ارسال برای دوستان</p><button className="btn-generate-results" onClick={generateStory} disabled={generating}><span className="material-symbols-outlined">{generating ? 'hourglass_top' : 'download'}</span>{generating ? 'در حال ساخت…' : 'ساخت و دانلود نتایج'}</button></footer>
      </section>
    </div>
  ), document.body);
}

export default ResultsStoryGenerator;
